import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'state';
import { useCommunicationService } from 'src/app/services/communication';
import { type AppMessage, MessageType } from 'src/app/utils/in-app-comunication/types';
import timerActions from 'actions/timer';

export type SectionConfig = {
  workTime: number;
  restTime: number;
  rounds: number;
};

export type SectionState = {
  currentRound: number;
  currentSection: 'Work' | 'Rest';
  elapsedTime: number;
  countdownTime: number;
  isCompleted: boolean;
  sectionProgress: number;
};

type UseSectionManagerProps = {
  timerId?: string;
  sectionConfig?: SectionConfig;
  finalDuration: number;
  autoStart?: boolean;
  onSectionComplete?: (sectionState: SectionState) => void;
  onWorkoutComplete?: () => void;
};

const useSectionManager = ({
  timerId,
  sectionConfig,
  finalDuration,
  autoStart = false,
  onSectionComplete,
  onWorkoutComplete,
}: UseSectionManagerProps) => {
  const dispatch = useDispatch();
  const comms = useCommunicationService();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(autoStart);
  const intervalRef = useRef<number | null>(null);
  const elapsedTimeRef = useRef(elapsedTime);
  const isRunningRef = useRef(isRunning);
  const workRoundsCompletedRef = useRef(false);

  const hasWorkRestSections = sectionConfig && (sectionConfig.workTime > 0 || sectionConfig.restTime > 0);

  const totalDuration =
    hasWorkRestSections && sectionConfig
      ? (sectionConfig.workTime + sectionConfig.restTime) * sectionConfig.rounds
      : finalDuration;

  const getCurrentSectionState = useCallback(
    (elapsed: number): SectionState => {
      if (!hasWorkRestSections) {
        const countdown = Math.max(0, finalDuration - elapsed);
        return {
          currentRound: 0,
          currentSection: 'Work',
          elapsedTime: elapsed,
          countdownTime: countdown,
          isCompleted: elapsed >= finalDuration,
          sectionProgress: Math.min(100, (elapsed / finalDuration) * 100),
        };
      }

      const { workTime, restTime, rounds } = sectionConfig;
      const timePerRound = workTime + restTime;
      const currentRound = Math.floor(elapsed / timePerRound);
      const timeInCurrentRound = elapsed % timePerRound;

      let currentSection: 'Work' | 'Rest';
      let countdownTime: number;
      let sectionProgress: number;

      if (timeInCurrentRound < workTime) {
        currentSection = 'Work';
        countdownTime = workTime - timeInCurrentRound;
        sectionProgress = Math.min(100, (timeInCurrentRound / workTime) * 100);
      } else {
        currentSection = 'Rest';
        countdownTime = timePerRound - timeInCurrentRound;
        sectionProgress = Math.min(100, ((timeInCurrentRound - workTime) / restTime) * 100);
      }

      const isCompleted = elapsed >= totalDuration;

      // When completed, always show "Rest" to indicate the workout is finished
      if (isCompleted) {
        currentSection = 'Rest';
        countdownTime = 0;
        sectionProgress = 100;
      }

      return {
        currentRound: Math.min(currentRound, rounds - 1),
        currentSection,
        elapsedTime: elapsed,
        countdownTime,
        isCompleted,
        sectionProgress,
      };
    },
    [hasWorkRestSections, sectionConfig, finalDuration, totalDuration],
  );

  const currentSectionState = getCurrentSectionState(elapsedTime);

  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  const checkSectionTransitions = useCallback(
    (newElapsed: number) => {
      if (!hasWorkRestSections) {
        return;
      }

      const { workTime, restTime } = sectionConfig;
      const timePerRound = workTime + restTime;
      const timeInCurrentRound = newElapsed % timePerRound;

      if (timeInCurrentRound === 0 && newElapsed > 0) {
        const newSectionState = getCurrentSectionState(newElapsed);
        onSectionComplete?.(newSectionState);
      } else if (timeInCurrentRound === workTime) {
        const newSectionState = getCurrentSectionState(newElapsed);
        onSectionComplete?.(newSectionState);
      }
    },
    [hasWorkRestSections, sectionConfig, getCurrentSectionState, onSectionComplete],
  );

  const checkWorkRoundsCompletion = useCallback(
    (newElapsed: number) => {
      if (!timerId || workRoundsCompletedRef.current) {
        return;
      }

      // For timers with work/rest sections: check if all work rounds are completed (excluding rest time)
      if (hasWorkRestSections && sectionConfig) {
        const { workTime, rounds } = sectionConfig;
        const totalWorkTime = workTime * rounds;

        if (newElapsed >= totalWorkTime) {
          workRoundsCompletedRef.current = true;
          dispatch(timerActions.setTimerWorkRoundsCompleted(timerId));
        }
      } else if (newElapsed >= finalDuration) {
        // For timers without work/rest sections: consider completed when timer finishes
        workRoundsCompletedRef.current = true;
        dispatch(timerActions.setTimerWorkRoundsCompleted(timerId));
      }
    },
    [hasWorkRestSections, sectionConfig, timerId, dispatch, finalDuration],
  );

  const startTimer = useCallback(() => {
    if (intervalRef.current === null) {
      intervalRef.current = window.setInterval(() => {
        const newElapsed = elapsedTimeRef.current + 1;

        checkSectionTransitions(newElapsed);

        // Send periodic update message every second when timer is running
        // Only send from presenter view to avoid duplicate messages
        if (timerId) {
          const params = new URLSearchParams(window.location.search);
          const isPresenterView = params.get('isPresenterView') !== 'false';

          if (isPresenterView) {
            comms.sendMessage({
              type: MessageType.TIMER_UPDATE,
              timerId,
              elapsedTime: newElapsed,
              isRunning: true,
              timestamp: Date.now(),
            });
          }
        }

        // Check if workout is complete
        if (newElapsed >= totalDuration) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsRunning(false);
          setElapsedTime(totalDuration);
          elapsedTimeRef.current = totalDuration;
          onWorkoutComplete?.();
        } else {
          // Pure state update - only set the next value
          setElapsedTime(newElapsed);
          elapsedTimeRef.current = newElapsed;
        }
      }, 1000);
    }
  }, [checkSectionTransitions, totalDuration, onWorkoutComplete, timerId, comms]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
      if (timerId) {
        const message = {
          type: MessageType.TIMER_PLAY,
          timerId,
          timestamp: Date.now(),
        };
        comms.sendMessage(message);
      }
    }
  }, [isRunning, timerId, comms]);

  const pause = useCallback(() => {
    if (isRunning) {
      setIsRunning(false);
      stopTimer();
      if (timerId) {
        const message = {
          type: MessageType.TIMER_PAUSE,
          timerId,
          elapsedTime: elapsedTime,
          timestamp: Date.now(),
        };
        comms.sendMessage(message);
      }
    }
  }, [isRunning, stopTimer, timerId, elapsedTime, comms]);

  const restart = useCallback(() => {
    stopTimer();
    setIsRunning(false);
    setElapsedTime(0);
    if (timerId) {
      const message = {
        type: MessageType.TIMER_RESTART,
        timerId,
        elapsedTime: finalDuration,
        timestamp: Date.now(),
      };
      comms.sendMessage(message);
    }
  }, [stopTimer, timerId, finalDuration, comms]);

  const reset = useCallback(() => {
    stopTimer();
    setIsRunning(false);
    setElapsedTime(0);
    workRoundsCompletedRef.current = false;
    if (timerId) {
      dispatch(timerActions.resetTimerCompletions());
    }
  }, [stopTimer, timerId, dispatch]);

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.SLIDESHOW_CHANGE) {
        reset();
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [reset, comms]);

  // Check work rounds completion when elapsedTime changes
  useEffect(() => {
    checkWorkRoundsCompletion(elapsedTime);
  }, [elapsedTime, checkWorkRoundsCompletion]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPresenterView = params.get('isPresenterView') !== 'false';
    const slideIndex = params.get('slideIndex');

    if (!isPresenterView && slideIndex && timerId) {
      setTimeout(() => {
        comms.sendMessage({
          type: MessageType.TIMER_STATE_REQUEST,
          slideIndex: Number.parseInt(slideIndex, 10),
          timestamp: Date.now(),
        });
      }, 500);
    }
  }, [timerId, comms]);

  useEffect(() => {
    if (!timerId) {
      return;
    }

    const handler = (msg: AppMessage) => {
      try {
        switch (msg.type) {
          case MessageType.TIMER_PLAY:
            setIsRunning(true);
            break;
          case MessageType.TIMER_PAUSE:
            setIsRunning(false);
            stopTimer();
            if (msg.elapsedTime !== undefined) {
              setElapsedTime(msg.elapsedTime);
            }
            break;
          case MessageType.TIMER_RESTART: {
            setIsRunning(false);
            setElapsedTime(0);
            break;
          }
          case MessageType.TIMER_SYNC_STATE:
            if (msg.elapsedTime !== undefined && msg.isRunning !== undefined) {
              setElapsedTime(msg.elapsedTime);
              setIsRunning(msg.isRunning);
            }
            break;
          case MessageType.TIMER_UPDATE:
            // Only update state if we're the slave view (not presenter)
            // This prevents the presenter from updating its own state from its own messages
            if (msg.timerId === timerId) {
              const params = new URLSearchParams(window.location.search);
              const isPresenterView = params.get('isPresenterView') !== 'false';

              if (!isPresenterView && msg.elapsedTime !== undefined) {
                // Stop local timer interval - slave should only update from presenter messages
                stopTimer();
                // Update state from the presenter's message
                setElapsedTime(msg.elapsedTime);
                // Ensure timer appears running if we're receiving updates
                if (!isRunning) {
                  setIsRunning(true);
                }
              }
            }
            break;
          case MessageType.TIMER_STATE_REQUEST: {
            // Only respond if we're the presenter view
            const params = new URLSearchParams(window.location.search);
            const isPresenterView = params.get('isPresenterView') !== 'false';

            if (isPresenterView) {
              if (msg.slideIndex !== undefined && timerId) {
                comms.sendMessage({
                  type: MessageType.TIMER_STATE_RESPONSE,
                  timerId,
                  elapsedTime: elapsedTimeRef.current,
                  isRunning: isRunningRef.current,
                  slideIndex: msg.slideIndex,
                  timestamp: Date.now(),
                });
              }
            }
            break;
          }
          case MessageType.TIMER_STATE_RESPONSE: {
            // Only update state if we're the athlete view
            const responseParams = new URLSearchParams(window.location.search);
            const isResponsePresenterView = responseParams.get('isPresenterView') !== 'false';

            if (!isResponsePresenterView) {
              if (msg.timerId === timerId && msg.slideIndex !== undefined) {
                if (msg.elapsedTime !== undefined && msg.isRunning !== undefined) {
                  setElapsedTime(msg.elapsedTime);
                  setIsRunning(msg.isRunning);
                }
              }
            }
            break;
          }
          default:
            break;
        }
      } catch (error) {
        console.error('Error processing timer message:', error);
      }
    };

    const unsubscribe = comms.onMessage(handler);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      stopTimer();
    };
  }, [timerId, comms, stopTimer, isRunning]);

  // Auto-start effect
  useEffect(() => {
    if (autoStart) {
      setIsRunning(true);
    }
  }, [autoStart]);

  // Timer start/stop effect
  // Only start local timer interval if we're the presenter view
  // Slaves rely on TIMER_UPDATE messages for synchronization
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPresenterView = params.get('isPresenterView') !== 'false';

    if (isRunning && isPresenterView) {
      startTimer();
    } else {
      stopTimer();
    }

    return () => stopTimer();
  }, [isRunning, startTimer, stopTimer]);

  return {
    elapsedTime,
    isRunning,
    currentSectionState,

    play,
    pause,
    restart,
    reset,

    hasWorkRestSections,
    totalDuration,
    isCompleted: currentSectionState.isCompleted,
  };
};

export default useSectionManager;
